import { useState, useEffect, useMemo, useCallback } from 'react';
import { useStateSelector } from 'hooks';
import { OrganizationModel, Version } from 'models';
import { getCurrentCooperative } from 'store/home/selectors';

import { startOfMonth, endOfMonth, format } from 'date-fns';
import { toDate, serviceDateFormat } from 'utils';

import services from 'services';

import { NetIncomeDetails } from 'components';

function buildURL(
  coopId: string,
  version: Version,
  startDate: Date,
  endDate: Date
) {
  const searchParams = `startDate=${format(
    startDate,
    serviceDateFormat
  )}&endDate=${format(endDate, serviceDateFormat)}&basedOn=${version.value}`;

  return `/api/portfolios/${coopId}/net-income?${searchParams}`;
}

interface NetIncomeState {
  version: {
    label: string;
    value: number;
  };
  startDate: Date | null;
  endDate: Date | null;
  loading: boolean;
  error: boolean;
}

const NetIncome = () => {
  const { coopId, coopName } = useStateSelector((state) => ({
    coopId: state.settings.current_cooperative,
    coopName: getCurrentCooperative(state)?.Name || null,
  }));

  const [state, setState] = useState<NetIncomeState>({
    version: {
      label: '#portal.investor.report.net-income.basedon.calculationsbased',
      value: 1,
    },
    startDate: null,
    endDate: null,
    loading: false,
    error: true,
  });

  useEffect(() => {
    (async function () {
      if (coopId) {
        try {
          setState((prevState) => ({
            ...prevState,
            loading: true,
            error: false,
          }));

          const organization = await fetchOrganization(coopId);
          const d = new Date();
          const endingDate = toDate(organization.PeriodEndingDate);

          setState((prevState) => ({
            ...prevState,
            startDate: endingDate ? startOfMonth(endingDate) : startOfMonth(d),
            endDate: endingDate ? endOfMonth(endingDate) : endOfMonth(d),
            loading: false,
          }));
        } catch (err) {
          console.error(err);

          setState((prevState) => ({
            ...prevState,
            loading: false,
            error: true,
          }));
        }
      }
    })();
  }, [coopId]);

  const fetchOrganization = async (coopId: string) => {
    const organization: OrganizationModel = (
      await services.get(`/api/portfolios/${coopId}/organization`)
    ).data.Data;

    return organization;
  };

  const handleChangeDate = useCallback((date: Date | null, name?: string) => {
    if (name) {
      setState((prevState) => ({
        ...prevState,
        [name]: date,
      }));
    }
  }, []);

  const handleChangeVersion = useCallback((version: Version) => {
    setState((prevState) => ({
      ...prevState,
      version,
    }));
  }, []);

  const url = useMemo(() => {
    if (coopId && state.startDate && state.endDate) {
      return buildURL(coopId, state.version, state.startDate, state.endDate);
    }

    return '';
  }, [coopId, state.version, state.startDate, state.endDate]);

  const fileName = useMemo(() => {
    return `investor_net-income__${coopName}`;
  }, [coopName]);

  return (
    <NetIncomeDetails
      {...state}
      fileName={fileName}
      url={url}
      handleChangeDate={handleChangeDate}
      handleChangeVersion={handleChangeVersion}
    />
  );
};

export default NetIncome;
